import Vue from "vue";
import EventBus from "@/eventBus";
import * as types from "./mutation-types";
import moment from "moment";

const actions = {};

// ACTIVITY
const activityActions = {
  async fetchActivities({ commit }, programId) {
    let url = `activities?programId=${programId}&enabled=1`;

    const response = await Vue.axios.get(url);
    const activities = response.data.data;

    commit(types.SET_LOADED_STATUS, { key: "activitiesLoaded", value: true });
    commit(types.SET_ACTIVITIES, activities);
  },

  async updateEnabledActivities({ commit, dispatch }, data) {
    const { programId, activityIds } = data;

    const response = await Vue.axios.post(`program-activities/${programId}`, {
      activityIds
    });
    const activities = response.data.data;

    commit(types.SET_LOADED_STATUS, { key: "activitiesLoaded", value: true });
    commit(types.SET_ACTIVITIES, activities);

    dispatch("members/fetchActivities", programId, { root: true });
  }
};

// PROGRAM
const programActions = {
  async fetchProgram({ commit }, programId = Vue.auth.user().programId) {
    const response = await Vue.axios.get(`programs/${programId}`);
    const program = response.data.data;
    const programType = program.type;

    if (program.tier.includes("core")) {
      commit("program/SET_TYPE_CORETIER", true, { root: true });
    } else if (program.tier.includes("plus")) {
      commit("program/SET_TYPE_PLUSTIER", true, { root: true });
    } else if (program.tier.includes("pro")) {
      commit("program/SET_TYPE_PROTIER", true, { root: true });
    }

    if (programType.includes("activate")) {
      commit(types.SET_TYPE_ACTIVATEPRODUCT, true);
    } else if (programType.includes("engage")) {
      commit(types.SET_TYPE_ENGAGEPRODUCT, true);
    } else if (programType.includes("thrive")) {
      commit(types.SET_TYPE_THRIVEPRODUCT, true);
    }

    if (program.incentiveIntegrationOn) {
      commit(types.SET_INCENTIVE_INTEGRATION, true);
    }
    // TODO do we need this?
    if (programType.includes("wellness")) {
      commit(types.SET_TYPE_WELLNESS, true);
    } else if (programType.includes("activity")) {
      commit(types.SET_TYPE_ACTIVITY, true);
    }

    commit(types.SET_PROGRAM_LOADED, true);
    commit(types.SET_PROGRAM, program);
  },

  storeProgram(_, payload) {
    return Vue.axios.post(`/programs`, payload).catch(error => {
      throw error.response;
    });
  },

  programSettingsUpdate({ commit }, payload) {
    let { programId, formData } = payload;

    return Vue.axios
      .post(`programs/${programId}`, formData)
      .then(response => {
        if (response && response.data.success) {
          Vue._.each(formData, (value, key) => {
            commit(types.UPDATE_META_VALUE, {
              key: key,
              value: value
            });
            commit(types.UPDATE_SETTINGS_COPY_VALUE, {
              key: key,
              value: value
            });
          });
          return response;
        }
      })
      .catch(error => {
        return error.response.data;
      });
  },

  setProgram({ commit }, program) {
    commit(types.SET_PROGRAM, program);

    if (program.tier.includes("core")) {
      commit("program/SET_TYPE_CORETIER", true, { root: true });
    } else if (program.tier.includes("plus")) {
      commit("program/SET_TYPE_PLUSTIER", true, { root: true });
    } else if (program.tier.includes("pro")) {
      commit("program/SET_TYPE_PROTIER", true, { root: true });
    }

    if (program.type.includes("activate")) {
      commit(types.SET_TYPE_ACTIVATEPRODUCT, true);
    } else if (program.type.includes("engage")) {
      commit(types.SET_TYPE_ENGAGEPRODUCT, true);
    } else if (program.type.includes("thrive")) {
      commit(types.SET_TYPE_THRIVEPRODUCT, true);
    }
  },

  updateCoreMeta({ commit }, data) {
    commit(types.UPDATE_META_VALUE, data);
  },

  updateProgramSettingsCopy({ commit }, data) {
    commit(types.UPDATE_SETTINGS_COPY_VALUE, data);
  },

  resetProgramSettingsCopy({ commit, getters }) {
    commit(types.SET_PROGRAM, getters["coreProgramMeta"]);
  },

  memberStatsDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let response = null;

    try {
      return Vue.axios.get(
        `programs/${programId}/member-stats-download?startDate=${startDate}&endDate=${endDate}`,
        {
          responseType: "arraybuffer"
        }
      );
    } catch (error) {
      response = error.response;
    }

    return response;
  },

  everyStepReportDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let params = { programId, startDate, endDate };
    return Vue.axios
      .get(`super/reports/every-step-daily`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  activeProgramsReport() {
    return Vue.axios
      .get(`super/reports/active-programs`)
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  pVsPChallengeStatsDownload(_, challengeId) {
    let params = { challengeId };
    return Vue.axios
      .get(`super/reports/program-vs-program`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  fetchAll(_, payload) {
    const { sortColumn, sortOrder, page, perPage = 7 } = payload;

    const field = sortColumn ? `&sortColumn=${sortColumn}` : "";
    const order = sortOrder ? `&sortOrder=${sortOrder}` : "";
    const thisPage = page ? `&page=${page}` : "";
    const perThisPage = perPage ? `&perPage=${perPage}` : "";

    return Vue.axios
      .get(`programs?${field}${order}${thisPage}${perThisPage}`)
      .then(response => response.data);
  },

  searchPrograms(_, query) {
    return Vue.axios
      .get(`programs/search?searchString=${query}`)
      .then(response => response.data);
  },

  fetchWellnessTips({ dispatch }, tipCategory) {
    return Vue.axios
      .get(`programs/wellness-tips/${tipCategory}`)
      .then(response => response.data)
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        throw error.response;
      });
  }
};

// PROGRAM MAPS
const mapActions = {
  async fetchProgramMaps({ commit }) {
    commit(types.SET_LOADED_STATUS, {
      key: "programMapsLoaded",
      value: false
    });

    // TODO logic: !getters["isCoreTier"] && Vue.auth.user().isAdmin
    const includeWellness = Vue.auth.user().isAdmin;

    const includePreventative = Vue.auth.user().isAdmin;

    const response = await Vue.axios.get(`programs/maps`, {
      params: {
        includeWellness: Number(includeWellness),
        includePreventative: Number(includePreventative)
      }
    });

    commit(types.SET_MAPS, response.data.data);
    commit(types.SET_LOADED_STATUS, {
      key: "programMapsLoaded",
      value: true
    });
  },

  async searchMaps({ commit }, data) {
    commit(types.SET_LOADED_STATUS, {
      key: "programMapsLoaded",
      value: false
    });
    const url = `programs/map-search?searchString=${data.query}&perPage=10`;

    const response = await Vue.axios.get(url);

    commit(types.SET_MAPS, response.data.data);
    commit(types.SET_LOADED_STATUS, {
      key: "programMapsLoaded",
      value: true
    });
  },

  mapMilestones({ dispatch }, mapId) {
    return Vue.axios
      .get(`maps/${mapId}/milestones`)
      .then(response => response.data)
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );
        throw error.response;
      });
  }
};

// PROGRAM TEAMS
const teamActions = {
  async fetchProgramTeams({ commit }, payload = {}) {
    let {
      programId = Vue.auth.user().programId,
      sortColumn,
      sortOrder,
      page,
      perPage = 5,
      all,
      notInChallengeId = 0
    } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "programTeamsLoaded",
      value: false
    });

    const response = await Vue.axios.get(`programs/${programId}/teams`, {
      params: { sortColumn, sortOrder, page, perPage, all, notInChallengeId }
    });

    EventBus.emitProgramTeamsResponse(response.data);

    if (response.data.pagination.currentPage == 1) {
      // TODO: refactor programTeams?!
      commit(types.SET_PROGRAM_TEAMS, response.data.data);
    }
    commit(types.SET_PROGRAM_TEAMS_RESPONSE_OBJECT, response.data);
    commit(types.SET_LOADED_STATUS, { key: "programTeamsLoaded", value: true });

    return response.data;
  },

  async searchProgramTeams(_, payload = {}) {
    const { programId, query, perPage, page } = payload;

    const params = { searchString: query, perPage, page };

    const response = await Vue.axios.get(`programs/${programId}/team-search`, {
      params
    });

    return response.data;
  },

  removeTeamFromProgramTeams({ commit, state }, teamId) {
    const index = Vue._.findIndex(state.programTeams, { id: teamId });

    commit(types.REMOVE_PROGRAM_TEAM, index);
  },

  addTeamToProgramTeams({ commit }, team) {
    commit(types.ADD_PROGRAM_TEAM, team);
  },

  updateTeamInProgramTeams({ commit }, team) {
    commit(types.UPDATE_PROGRAM_TEAM, team);
  },

  allUsersDownload(_, payload) {
    const { programId } = payload;

    return Vue.axios
      .get(`programs/${programId}/all-users-download`, {
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  userAssessmentStatusDownload(_, payload) {
    const { programId } = payload;

    return Vue.axios
      .get(`mental-wellness/assessment-status-report/${programId}`, {
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  userAssessmentQuestionsDownload() {
    return Vue.axios
      .get(`mental-wellness/assessment-questions-report`, {
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  teamSummaryDownload(_, payload) {
    const { programId, startDate, endDate, isAllTeamSummary } = payload;

    let params = { startDate, endDate, isAllTeamSummary };
    return Vue.axios
      .get(`programs/${programId}/team-summary-download`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response.data)
      .catch(error => {
        throw error.response;
      });
  },

  uploadCustomPointCsv(_, payload) {
    const { programId, data } = payload;
    return Vue.axios
      .post(`program-admin/${programId}/upload-custom-point-csv`, data)
      .then(response => response.data)
      .catch(error => {
        throw error.response;
      });
  },
  pointsSummaryDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let params = { startDate, endDate };
    return Vue.axios
      .get(`programs/${programId}/points-summary-download`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  async badgesStatsDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let params = { startDate, endDate };
    return Vue.axios
      .get(`programs/${programId}/badges-stats-download`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(async error => {
        var arrayBufferView = new Uint8Array(error.response.data);
        var blob = new Blob([arrayBufferView]);
        var jsonString = await blob.text();
        var response = JSON.parse(jsonString);
        return response;
      });
  },

  wellnessMetricsDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let params = { startDate, endDate };
    return Vue.axios
      .get(`programs/${programId}/wellness-metrics-download`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => {
        throw error.response;
      });
  },

  organizationSummaryDownload(_, payload) {
    const { programId, startDate, endDate } = payload;
    let params = { startDate, endDate };
    return Vue.axios
      .get(`programs/${programId}/organization-summary-download`, {
        params,
        responseType: "arraybuffer"
      })
      .then(response => response)
      .catch(error => error);
  }
};

// PROGRAM MEMBERS
const memberActions = {
  async fetchMembers({ commit }, payload) {
    const {
      programId,
      sortColumn,
      sortOrder,
      page,
      perPage,
      notInTeam,
      notInChallengeId,
      inOrganizationId
    } = payload;

    commit(types.SET_LOADED_STATUS, { key: "membersLoaded", value: false });
    let params = {
      sortColumn: sortColumn ? sortColumn : null,
      sortOrder: sortOrder ? sortOrder : null,
      page: page ? page : null,
      perPage: perPage ? perPage : null,
      notInChallengeId: notInChallengeId ? notInChallengeId : null,
      inOrganizationId: inOrganizationId ? inOrganizationId : null
    };

    const fetchUrl = notInTeam ? "members-not-in-team" : "members";

    const response = await Vue.axios.get(`programs/${programId}/${fetchUrl}`, {
      params
    });

    // this looks redundant, but setting the response to two different objects
    // allows for one to be used for searching members in the admin, and
    // the other to be used for displaying member stats in the
    // Sitewide view
    commit(types.SET_ADMIN_MEMBERS_RESPONSE_OBJECT, response.data);
    commit(types.SET_MEMBERS_RESPONSE_OBJECT, response.data);

    commit(types.SET_LOADED_STATUS, { key: "membersLoaded", value: true });
    commit(types.SET_LOADED_STATUS, {
      key: "membersInitialLoadComplete",
      value: true
    });
  },

  setAdminMembersResponseObject({ commit }, responseObject) {
    commit(types.SET_ADMIN_MEMBERS_RESPONSE_OBJECT, responseObject);
  },

  setMembersResponseObject({ commit }, responseObject) {
    commit(types.SET_MEMBERS_RESPONSE_OBJECT, responseObject);
  },

  async searchMembers({ commit }, data) {
    commit(types.SET_LOADED_STATUS, { key: "membersLoaded", value: false });
    let params = {
      searchString: data.query,
      page: data.hasOwnProperty("page") ? data.page : null,
      perPage: data.hasOwnProperty("perPage") ? data.perPage : null,
      notFriends: data.hasOwnProperty("notFriends") ? data.notFriends : null,
      notInTeam: data.hasOwnProperty("notInTeam") ? data.notInTeam : null,
      notInChallengeId: data.hasOwnProperty("notInChallengeId")
        ? data.notInChallengeId
        : null,
      inOrganizationId: data.hasOwnProperty("inOrganizationId")
        ? data.inOrganizationId
        : null
    };

    const response = await Vue.axios.get(
      `programs/${data.programId}/member-search`,
      { params }
    );

    commit(types.SET_LOADED_STATUS, { key: "membersLoaded", value: true });

    return response.data;
  },

  removeMemberFromList({ commit }, member) {
    commit(types.REMOVE_MEMBER, member.id);
  },

  addMemberToList({ commit }, member) {
    commit(types.ADD_MEMBER, member);
  }
};

const challengeActions = {
  fetchChallenges({ commit }, payload) {
    const { programId } = payload;

    commit(types.SET_LOADED_STATUS, { key: "challengesLoaded", value: false });

    return Vue.axios.get(`programs/${programId}/challenges`).then(response => {
      commit(types.SET_CHALLENGES, response.data.data);
      commit(types.SET_LOADED_STATUS, { key: "challengesLoaded", value: true });
    });
  },

  async fetchAllChallenges({ commit }, payload) {
    const { programId } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "allChallengesLoaded",
      value: false
    });

    let fetchLink = `programs/${programId}/challenges?showAll=1`;
    let allChallenges = [];
    while (fetchLink) {
      let response = await Vue.axios.get(fetchLink);
      allChallenges.push(...response.data.data);
      //fetch challenges from all pages
      fetchLink = response.data.pagination.links.next;
    }
    commit(types.SET_ALL_CHALLENGES, allChallenges);
    commit(types.SET_LOADED_STATUS, {
      key: "allChallengesLoaded",
      value: true
    });
  },

  fetchFeaturedChallenge({ commit }, programId) {
    commit(types.SET_LOADED_STATUS, {
      key: "featuredChallengesLoaded",
      value: false
    });
    commit(types.SET_LOADED_STATUS, {
      key: "currentFeaturedChallengeLoaded",
      value: false
    });
    return Vue.axios
      .get(`programs/${programId}/challenges?featured=1`)
      .then(response => {
        commit(
          types.SET_FEATURED_CHALLENGES,
          Vue._.get(response, "data.data", [])
        );
        commit(types.SET_LOADED_STATUS, {
          key: "featuredChallengesLoaded",
          value: true
        });
        commit(
          types.SET_FEATURED_CHALLENGE,
          Vue._.get(response, "data.data[0]", [])
        );
        commit(types.SET_LOADED_STATUS, {
          key: "currentFeaturedChallengeLoaded",
          value: true
        });
      });
  },

  fetchAllFeaturedChallenge({ commit }, programId) {
    commit(types.SET_LOADED_STATUS, {
      key: "allFeaturedChallengesLoaded",
      value: false
    });
    return Vue.axios
      .get(`programs/${programId}/challenges?showAllFeatured=1`)
      .then(response => {
        commit(
          types.SET_ALL_FEATURED_CHALLENGES,
          Vue._.get(response, "data.data", [])
        );
        commit(types.SET_LOADED_STATUS, {
          key: "allFeaturedChallengesLoaded",
          value: true
        });
      });
  },

  fetchActiveChallenges({ commit }, payload) {
    const { programId, creatorRoleInfo } = payload;

    let params = {
      status: "active",
      creatorRoleInfo
    };
    commit(types.SET_LOADED_STATUS, {
      key: "activeChallengesLoaded",
      value: false
    });

    return Vue.axios
      .get(`programs/${programId}/challenges`, { params })
      .then(response => {
        commit(types.SET_ACTIVE_CHALLENGES, response.data);
        commit(types.SET_LOADED_STATUS, {
          key: "activeChallengesLoaded",
          value: true
        });
      });
  },

  fetchPendingChallenges({ commit }, payload) {
    const { programId, creatorRoleInfo } = payload;

    let params = {
      status: "pending",
      creatorRoleInfo
    };

    commit(types.SET_LOADED_STATUS, {
      key: "pendingChallengesLoaded",
      value: false
    });

    return Vue.axios
      .get(`programs/${programId}/challenges`, { params })
      .then(response => {
        commit(types.SET_PENDING_CHALLENGES, response.data);
        commit(types.SET_LOADED_STATUS, {
          key: "pendingChallengesLoaded",
          value: true
        });
      });
  },

  fetchArchivedChallenges({ commit }, payload) {
    const { programId, creatorRoleInfo } = payload;

    let params = {
      status: "archived",
      creatorRoleInfo
    };

    return Vue.axios
      .get(`programs/${programId}/challenges`, { params })
      .then(response => {
        commit(types.SET_ARCHIVED_CHALLENGES, response.data);
        commit(types.SET_LOADED_STATUS, {
          key: "archivedChallengesLoaded",
          value: true
        });
      });
  },

  removeArchivedChallengeFromList({ commit, state }, challengeId) {
    const index = Vue._.findIndex(state.archivedChallenges.data, {
      id: challengeId
    });

    commit(types.REMOVE_ARCHIVED_CHALLENGE, index);
  }
};

const journalActions = {
  fetchJournals({ commit }, payload) {
    let { programId, photosOnly, friends, page, resetList } = payload;

    commit(types.SET_LOADED_STATUS, { key: "journalsLoaded", value: false });

    // if we change our filters, then we need to blow away our list of journal entries.
    if (resetList === true) {
      commit(types.RESET_JOURNALS);
    }

    const startDate = moment()
      .subtract(3, "months")
      .format("YYYY-MM-DD");
    friends = friends ? `&friends=1` : "";
    photosOnly = photosOnly ? `&photosOnly=1` : "";
    page = page ? `&page=${page}` : "";

    return Vue.axios
      .get(
        `programs/${programId}/journals?perPage=10&startDate=${startDate}${photosOnly}${friends}${page}`
      )
      .then(response => {
        let responseData = response.data.data;

        // see if we've received any more data.
        if (responseData.length === 0) {
          commit(types.IS_JOURNAL_LAST_PAGE, true);
        } else {
          commit(types.SET_JOURNALS, responseData);
        }

        commit(types.SET_LOADED_STATUS, { key: "journalsLoaded", value: true });
      });
  }
};

const emailActions = {
  emailAdministrator(_, payload) {
    const { programId, body } = payload;

    return Vue.axios.post(`programs/${programId}/admin-email`, { body });
  },
  async fetchArchivedEmails(_, payload) {
    const { programId, page, sortColumn, sortOrder } = payload;

    let params = { page, sortColumn, sortOrder };

    return Vue.axios
      .get(`email-archive/${programId}`, { params })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw error.response;
      });
  }
};

const dataActions = {
  async fetchStepData({ commit }, programId) {
    const response = await Vue.axios.get(`programs/${programId}/step-data`);

    commit(types.SET_STEP_DATA, response.data.data);
    commit(types.SET_LOADED_STATUS, { key: "stepDataLoaded", value: true });
  },

  async fetchLeaderboardData({ commit }, programId) {
    const response = await Vue.axios.get(`programs/${programId}/leaderboard`);

    commit(types.SET_LEADERBOARD, response.data);
    commit(types.SET_LOADED_STATUS, { key: "leaderboardLoaded", value: true });

    return response.data;
  },

  async fetchAggregateData({ commit }, programId) {
    let response = null;

    try {
      const response = await Vue.axios.get(
        `programs/${programId}/aggregate-data`
      );

      commit(types.SET_AGGREGATE_DATA, response.data.data);
      commit(types.SET_LOADED_STATUS, {
        key: "aggregateDataLoaded",
        value: true
      });
    } catch (error) {
      response = error.response;
    }

    return response;
  },

  fetchHealthData(_, programId) {
    return Vue.axios
      .get(`programs/${programId}/health-data`)
      .then(response => response.data.data);
  },

  fetchAllProgramAggregate() {
    return Vue.axios
      .get("programs/all-program-aggregate")
      .then(response => response.data.data);
  }
};

const adminActions = {
  makeAdmin({ dispatch }, payload) {
    const { memberId } = payload;

    return Vue.axios.post(`program-admins`, { memberId }).catch(error => {
      const response = error.response.data.error;
      dispatch(
        "snackBar",
        {
          duration: 10000,
          queue: false,
          message: response.message || response.fields.memberId[0],
          type: "is-danger"
        },
        { root: true }
      );
    });
  },

  revokeAdmin({ dispatch }, payload) {
    const { programAdminId } = payload;

    return Vue.axios.delete(`program-admins/${programAdminId}`).catch(error => {
      const response = error.response.data.error;
      dispatch(
        "snackBar",
        {
          duration: 10000,
          queue: false,
          message: response.message || response.fields.programAdminId[0],
          type: "is-danger"
        },
        { root: true }
      );
    });
  },

  programAdminLog(_, payload = { page: 1 }) {
    const { page } = payload;

    return Vue.axios
      .get(`programs/${Vue.auth.user().programId}/admin-log`, {
        params: { page, perPage: 5 }
      })
      .then(response => response.data);
  }
};

const questionsActions = {
  fetchProgramQuestions({ commit }, payload = {}) {
    const { programId = Vue.auth.user().programId } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "programQuestionsLoaded",
      value: false
    });

    return Vue.axios
      .get(`programs/${programId}/questions`, {})
      .then(response => {
        commit(types.SET_PROGRAM_QUESTIONS, response.data.data);
        commit(types.SET_LOADED_STATUS, {
          key: "programQuestionsLoaded",
          value: true
        });
        return response.data;
      });
  },

  addProgramQuestion({ commit, dispatch }, payload = {}) {
    return Vue.axios
      .post(`questions`, payload)
      .then(response => {
        commit(types.ADD_PROGRAM_QUESTION, response.data.data);
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: response.data.data.draft
              ? "The question's draft has been created successfully."
              : "The question has been created successfully.",
            type: "is-success"
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  updateProgramQuestion({ commit, dispatch }, payload) {
    const { questionId } = payload;
    return Vue.axios
      .put(`questions/${questionId}`, payload)
      .then(response => {
        commit(types.UPDATE_PROGRAM_QUESTION, response.data.data);
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: "The question has been updated successfully.",
            type: "is-success"
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  deleteProgramQuestion({ commit, dispatch }, payload) {
    const { questionId } = payload;
    return Vue.axios
      .delete(`questions/${questionId}`)
      .then(response => {
        commit(types.REMOVE_PROGRAM_QUESTION, questionId);
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: "The question has been deleted successfully.",
            type: "is-success"
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  activateOrDeactivateProgramQuestion({ commit, dispatch }, payload) {
    const { questionId, active } = payload;
    return Vue.axios
      .put(`questions/${questionId}`, payload)
      .then(response => {
        Array.isArray(response.data.data)
          ? commit(types.UPDATE_PROGRAM_QUESTIONS, response.data.data)
          : commit(types.UPDATE_PROGRAM_QUESTION, response.data.data);

        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: `The question has been ${
              active ? "enabled" : "disabled"
            } successfully.`,
            type: "is-success"
          },
          { root: true }
        );

        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  archiveOrUnarchiveProgramQuestion({ commit, dispatch }, payload) {
    const { questionId, active } = payload;
    return Vue.axios
      .put(`questions/${questionId}`, payload)
      .then(response => {
        Array.isArray(response.data.data)
          ? commit(types.UPDATE_PROGRAM_QUESTIONS, response.data.data)
          : commit(types.UPDATE_PROGRAM_QUESTION, response.data.data);

        dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: `The question has been ${
              active ? "unarchived" : "archived"
            } successfully.`,
            type: "is-success"
          },
          { root: true }
        );

        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  deactivateAllProgramQuestions({ commit, dispatch }, payload) {
    const { programId } = payload;
    return Vue.axios
      .post(`questions/${programId}/deactivate-all`, payload)
      .then(response => {
        commit(types.UPDATE_PROGRAM_QUESTIONS, response.data.data);
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message: "All active questions have been disabled successfully.",
            type: "is-success"
          },
          { root: true }
        );
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  shiftQuestionsOrder({ commit, dispatch }, payload) {
    const { questionId } = payload;
    return Vue.axios
      .put(`questions/${questionId}`, payload)
      .then(response => {
        commit(types.UPDATE_PROGRAM_QUESTIONS, response.data.data);
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  },

  saveUserAnswers({ dispatch }, payload = {}) {
    return Vue.axios
      .post(`questions/save-answers`, payload)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            duration: 5000,
            queue: false,
            message:
              "There was an error. Please try again or contact wellness@be-engaged.co.",
            type: "is-danger"
          },
          { root: true }
        );
        throw error.response;
      });
  }
};

// PROGRAM ORGANIZATIONS
const organizationsActions = {
  async fetchProgramOrganizations({ commit }, payload = {}) {
    let { programId } = payload;

    commit(types.SET_LOADED_STATUS, {
      key: "programOrganizationsLoaded",
      value: false
    });

    const response = await Vue.axios.get(`programs/${programId}/organizations`);

    commit(types.SET_PROGRAM_ORGANIZATIONS, response.data.data);

    commit(types.SET_LOADED_STATUS, {
      key: "programOrganizationsLoaded",
      value: true
    });
  }
};

Object.assign(
  actions,
  activityActions,
  programActions,
  mapActions,
  teamActions,
  memberActions,
  challengeActions,
  journalActions,
  emailActions,
  dataActions,
  adminActions,
  questionsActions,
  organizationsActions
);

export default actions;
